// PO-GST-01 · P2-D1-C · payload mapping only. All GST maths and validation stay on the backend.
export const CHARGE_TREATMENTS = [
  { value: "INCLUDED_IN_GOODS", label: "Included in goods" },
  { value: "SEPARATE_SERVICE", label: "Separate taxable service" },
  { value: "NON_TAXABLE", label: "Non-taxable" },
];
export const CHARGE_TYPES = [
  { code: "FREIGHT", label: "Freight" },
  { code: "PACKING", label: "Packing" },
  { code: "OTHER", label: "Other charge" },
];
export const NON_TAXABLE_CLASSES = ["NIL_RATED", "EXEMPT", "NON_GST"];
export const STATE_CODES = [
  ["01", "Jammu & Kashmir"], ["02", "Himachal Pradesh"], ["03", "Punjab"], ["04", "Chandigarh"],
  ["05", "Uttarakhand"], ["06", "Haryana"], ["07", "Delhi"], ["08", "Rajasthan"], ["09", "Uttar Pradesh"],
  ["10", "Bihar"], ["11", "Sikkim"], ["12", "Arunachal Pradesh"], ["13", "Nagaland"], ["14", "Manipur"],
  ["15", "Mizoram"], ["16", "Tripura"], ["17", "Meghalaya"], ["18", "Assam"], ["19", "West Bengal"],
  ["20", "Jharkhand"], ["21", "Odisha"], ["22", "Chhattisgarh"], ["23", "Madhya Pradesh"], ["24", "Gujarat"],
  ["26", "Dadra & Nagar Haveli and Daman & Diu"], ["27", "Maharashtra"], ["29", "Karnataka"], ["30", "Goa"],
  ["31", "Lakshadweep"], ["32", "Kerala"], ["33", "Tamil Nadu"], ["34", "Puducherry"],
  ["35", "Andaman & Nicobar"], ["36", "Telangana"], ["37", "Andhra Pradesh"], ["38", "Ladakh"],
  ["97", "Other Territory"],
];

export const numOrNull = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));
const rateNum = (v) => { const n = numOrNull(v); return n === 0 ? null : n; };

// Create: confirmed 0 only via the checkbox; a typed/blank 0 is never a confirmed zero.
export function createLineGst(l) {
  if (l.zero_confirmed) return { tax_pct: 0, gst_rate_source: "edited" };
  const n = rateNum(l.tax_pct);
  if (n == null) return l.gst_rate_source === "edited" ? { tax_pct: null, gst_rate_source: "edited" } : { tax_pct: null };
  return { tax_pct: n, ...(l.gst_rate_source === "edited" ? { gst_rate_source: "edited" } : {}) };
}

// Edit: send only fields the buyer changed (explicit presence, incl. intentional clearing).
export function editLineGst(el, orig) {
  const out = {};
  const hsn = (el.hsn_code || "").trim();
  if (hsn !== (orig.hsn_code || "")) out.hsn_code = hsn;
  if (el.gst_dirty) {
    const n = rateNum(el.tax_pct);
    Object.assign(out, el.zero_confirmed ? { tax_pct: 0, gst_rate_source: "edited" }
      : n == null ? { tax_pct: null } : { tax_pct: n, gst_rate_source: "edited" });
  }
  ["discount_pct", "discount_amt"].forEach((k) => {
    const v = numOrNull(el[k]);
    if (v !== (orig[k] ?? null)) out[k] = v;
  });
  return out;
}

export const chargesPayload = (charges) => (charges || []).map((c) => ({
  code: (c.code || "").trim(), label: c.label || "", amount: numOrNull(c.amount), treatment: c.treatment,
  ...(c.treatment === "SEPARATE_SERVICE" ? { sac_code: (c.sac_code || "").trim(), gst_rate: numOrNull(c.gst_rate) } : {}),
  ...(c.treatment === "NON_TAXABLE" ? { non_taxable_reason: c.non_taxable_reason || "",
    non_taxable_classification: c.non_taxable_classification || "" } : {}),
}));

export const gstHeaderPayload = (h) => ({
  place_of_supply_state_code: h.place_of_supply_state_code || "",
  company_is_receiving_location: h.company_is_receiving_location === true,
  reverse_charge: h.reverse_charge === true,
  charges: chargesPayload(h.charges),
});

export const gstHeaderFromPo = (po) => ({
  place_of_supply_state_code: po.place_of_supply_state_code || "",
  company_is_receiving_location: po.company_is_receiving_location === true,
  reverse_charge: po.reverse_charge === true,
  charges: (po.charges || []).map((c) => ({ ...c, amount: c.amount ?? "", gst_rate: c.gst_rate ?? "" })),
});

export function gstHeaderDiff(h, po) {
  const next = gstHeaderPayload(h);
  const prev = gstHeaderPayload(gstHeaderFromPo(po));
  return Object.fromEntries(Object.entries(next).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(prev[k])));
}

export const isGstControlled = (po) => po?.gst_control?.enabled === true;

// Saved snapshot → same display shape as the preview response (read-only projection).
export function savedResult(po) {
  const e = po?.tax_snapshot;
  if (!e?.snapshot) return null;
  return { kind: "saved", tax_status: e.snapshot.tax_status, totals: e.snapshot.totals,
    findings: e.snapshot.findings || [], blockers: [], classification: e.snapshot.classification,
    seq: e.seq, revision: e.po_revision, current: e.po_revision === Number(po.revision || 0) };
}

const fKey = (f) => `${f.code}|${f.line_no ?? ""}|${f.charge_code ?? ""}`;
export function previewResult(p) {
  if (!p) return null;
  const seen = new Set((p.findings || []).map(fKey));
  return { kind: "preview", tax_status: p.tax_status, totals: p.totals, findings: p.findings || [],
    blockers: (p.save_blockers || []).filter((f) => !seen.has(fKey(f))), classification: p.classification,
    seq: p.seq_on_save, revision: p.po_revision };
}
